import { css } from "@repo/styled-system/css";

/**
 * The SINGLE source of the live-service visual language (live floor + door), token-based and
 * theme-aware. Everything the live surfaces paint — cards, the state badge, the notice/alert banner,
 * the segmented Plan/Liste toggle, the arm bar, the floor-map tables, the compact list, occupancy
 * meters, timeline rows and the walk-in form — lives here so FloorMap / DoorView / WalkInForm /
 * LiveServicePage share one look instead of three drifting `css()` objects. Tap targets are >= 44px
 * (RGAA baseline); the four table-state tints map onto the platform `booking.*` palette so a state
 * reads at a glance in both themes, and meaning is always carried by a LABEL + shape, never hue alone.
 */

/** The four derived/recorded table-state tint keys (from `lateness.ts` `tableStateTint`). */
export type Tint = "available" | "reserved" | "occupied" | "cleaning";

// --- Shared card shell (matches the polished waitlist / bookings pages) -------------------------
const card = css({
  borderWidth: "1px",
  borderColor: "border.default",
  rounded: "lg",
  bg: "bg.default",
  overflow: "hidden",
});
const cardHead = css({
  display: "flex",
  alignItems: "center",
  gap: "3",
  flexWrap: "wrap",
  px: "4",
  py: "3",
  borderBottomWidth: "1px",
  borderColor: "border.subtle",
  bg: "bg.subtle",
});
const cardTitle = css({ fontSize: "md", fontWeight: "semibold" });
const cardBody = css({ p: { base: "3", md: "4" } });
const countPill = css({
  fontSize: "xs",
  fontWeight: "medium",
  color: "fg.muted",
  bg: "bg.muted",
  rounded: "full",
  px: "2",
  py: "0.5",
  minW: "6",
  textAlign: "center",
});

// --- Token-driven state badge (booking.* palette; label + dot carry meaning, not hue) ----------
export const badgeBase = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "1",
  px: "2",
  py: "0.5",
  rounded: "md",
  borderWidth: "1px",
  fontSize: "2xs",
  fontWeight: "medium",
  whiteSpace: "nowrap",
});

/**
 * Tint -> `booking.*` family. Colours are chosen to PRESERVE the live floor's established reading
 * (occupied = green, reserved = blue, cleaning = amber, available = neutral) while sourcing every
 * value from the design-system tokens instead of inline hex. Literal `css()` calls so Panda extracts
 * each palette statically.
 */
export const stateBadgeClass: Record<Tint, string> = {
  available: css({ bg: "bg.subtle", color: "fg.muted", borderColor: "border.default" }),
  reserved: css({
    bg: "booking.seated.bg",
    color: "booking.seated.fg",
    borderColor: "booking.seated.border",
  }),
  occupied: css({
    bg: "booking.confirmed.bg",
    color: "booking.confirmed.fg",
    borderColor: "booking.confirmed.border",
  }),
  cleaning: css({
    bg: "booking.pending.bg",
    color: "booking.pending.fg",
    borderColor: "booking.pending.border",
  }),
};
export const stateDotClass: Record<Tint, string> = {
  available: css({ color: "fg.subtle" }),
  reserved: css({ color: "booking.seated.solid" }),
  occupied: css({ color: "booking.confirmed.solid" }),
  cleaning: css({ color: "booking.pending.solid" }),
};

/** The wash + border a floor-map table / list row wears for its state (crisp, editor-consistent). */
export const tableTint: Record<Tint, string> = {
  available: css({ bg: "bg.default", borderColor: "border.emphasized", color: "fg.default" }),
  reserved: css({
    bg: "booking.seated.bg",
    borderColor: "booking.seated.solid",
    color: "booking.seated.fg",
  }),
  occupied: css({
    bg: "booking.confirmed.bg",
    borderColor: "booking.confirmed.solid",
    color: "booking.confirmed.fg",
  }),
  cleaning: css({
    bg: "booking.pending.bg",
    borderColor: "booking.pending.solid",
    color: "booking.pending.fg",
  }),
};

// --- The live layout / controls -----------------------------------------------------------------
export const liveStyles = {
  card,
  cardHead,
  cardTitle,
  cardBody,
  countPill,
  page: css({ p: { base: "3", md: "5" }, maxW: "5xl", mx: "auto" }),
  header: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "3",
    flexWrap: "wrap",
    mb: "4",
  }),
  title: css({ fontSize: "2xl", fontWeight: "bold" }),
  subtitle: css({ color: "fg.muted", fontSize: "sm" }),
  toolbar: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "3",
    flexWrap: "wrap",
    mb: "3",
  }),

  // A consistent role="alert"/output banner (replaces the bespoke amber `notice`).
  alert: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "2",
    bg: "booking.pending.bg",
    color: "booking.pending.fg",
    borderWidth: "1px",
    borderColor: "booking.pending.border",
    rounded: "md",
    px: "3",
    py: "2",
    mb: "3",
    fontSize: "sm",
  }),

  section: css({ mb: "5" }),
  sectionTitle: css({ fontSize: "lg", fontWeight: "semibold", mb: "2" }),

  // Segmented Plan / Liste toggle.
  segmented: css({
    display: "inline-flex",
    p: "0.5",
    gap: "0.5",
    rounded: "lg",
    borderWidth: "1px",
    borderColor: "border.default",
    bg: "bg.subtle",
  }),
  segment: css({
    minH: "40px",
    px: "4",
    rounded: "md",
    fontSize: "sm",
    fontWeight: "medium",
    color: "fg.muted",
    cursor: "pointer",
    bg: "transparent",
    _hover: { color: "fg.default" },
  }),
  segmentActive: css({
    bg: "bg.default",
    color: "fg.default",
    borderWidth: "1px",
    borderColor: "border.default",
    fontWeight: "semibold",
  }),

  // The "tap a table" prompt while a booking is armed — STICKY so it stays visible on scroll.
  armBar: css({
    position: "sticky",
    top: "2",
    zIndex: "10",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "2",
    mb: "2",
    px: "3",
    py: "2",
    rounded: "md",
    fontWeight: "medium",
    fontSize: "sm",
    colorPalette: "accent",
    bg: "colorPalette.subtle",
    color: "colorPalette.text",
    borderWidth: "1px",
    borderColor: "colorPalette.default",
    boxShadow: "sm",
  }),

  // Compact / secondary occupancy meters (the plan + arrivals are the focus).
  meters: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
    gap: "2",
  }),
  meter: css({
    borderWidth: "1px",
    borderColor: "border.subtle",
    rounded: "md",
    px: "3",
    py: "2",
    bg: "bg.subtle",
  }),
  meterHead: css({
    display: "flex",
    justifyContent: "space-between",
    gap: "2",
    fontSize: "xs",
    fontWeight: "medium",
    mb: "1",
  }),
  meterTrack: css({ h: "4px", rounded: "full", bg: "bg.muted", overflow: "hidden" }),
  meterFill: css({ h: "full", bg: "booking.confirmed.solid", transition: "width 0.3s" }),
  meterFillWarn: css({ bg: "booking.pending.solid" }),
  meterFillOver: css({ bg: "booking.noShow.solid" }),
  meterWarn: css({ color: "booking.pending.fg", fontSize: "2xs", fontWeight: "medium" }),
  meterOver: css({ color: "booking.noShow.fg", fontSize: "2xs", fontWeight: "bold" }),

  // --- Compact LISTE view (tables grouped by zone) ----------------------------------------------
  listZone: css({ mb: "3" }),
  listZoneHead: css({
    fontSize: "xs",
    fontWeight: "semibold",
    letterSpacing: "wide",
    textTransform: "uppercase",
    color: "fg.muted",
    mb: "1",
  }),
  listGrid: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
    gap: "2",
  }),
  listRow: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "2",
    width: "full",
    minH: "56px",
    px: "3",
    py: "2",
    rounded: "md",
    borderWidth: "1px",
    textAlign: "left",
    cursor: "pointer",
    _disabled: { cursor: "default", opacity: 0.85 },
    _focusVisible: {
      outlineWidth: "2px",
      outlineStyle: "solid",
      outlineColor: "border.emphasized",
      outlineOffset: "1px",
    },
  }),
  listRowTarget: css({
    boxShadow: "0 0 0 2px var(--colors-color-palette-default)",
    colorPalette: "accent",
  }),
  listMain: css({ display: "flex", flexDir: "column", gap: "0.5", minW: "0" }),
  listLabel: css({ fontWeight: "semibold", fontSize: "sm" }),
  listMeta: css({ fontSize: "xs", color: "fg.muted" }),

  // --- Timeline / arrivals rows -----------------------------------------------------------------
  rows: css({ display: "flex", flexDir: "column", gap: "2", listStyle: "none", p: "0", m: "0" }),
  row: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "2",
    p: "3",
    rounded: "md",
    borderWidth: "1px",
    borderColor: "border.default",
    bg: "bg.default",
  }),
  rowMain: css({ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "2", minW: "0" }),
  rowTime: css({ fontWeight: "bold", fontVariantNumeric: "tabular-nums" }),
  rowGuest: css({ fontWeight: "medium" }),
  rowMeta: css({ color: "fg.muted", fontSize: "sm" }),
  rowActions: css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "center" }),
  confirmRow: css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "center" }),
  confirmText: css({ fontSize: "sm" }),
  pending: css({ fontSize: "2xs", fontWeight: "medium", color: "booking.pending.fg" }),
  late: css({ fontSize: "2xs", fontWeight: "bold", color: "booking.noShow.fg" }),
  allergy: css({ fontSize: "2xs", fontWeight: "medium", color: "booking.review.fg" }),
  empty: css({ color: "fg.muted", fontSize: "sm", py: "2" }),

  // --- Door arrivals + walk-in form -------------------------------------------------------------
  doorPage: css({ p: { base: "3", md: "4" }, maxW: "2xl", mx: "auto" }),
  arrival: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "2",
    p: "3",
    rounded: "lg",
    borderWidth: "1px",
    borderColor: "border.default",
    bg: "bg.default",
  }),
  arrived: css({
    fontSize: "xs",
    fontWeight: "bold",
    color: "booking.confirmed.fg",
  }),
  walkInBar: css({ display: "flex", flexDir: "column", gap: "2", mb: "4" }),
  walkInForm: css({
    display: "flex",
    flexDir: "column",
    gap: "3",
    p: "4",
    mb: "4",
    rounded: "lg",
    borderWidth: "1px",
    borderColor: "border.default",
    bg: "bg.subtle",
  }),
  field: css({ display: "flex", flexDir: "column", gap: "1" }),
  fieldLabel: css({ fontSize: "sm", fontWeight: "medium" }),
  input: css({
    px: "3",
    py: "2",
    minH: "44px",
    rounded: "md",
    borderWidth: "1px",
    borderColor: "border.default",
    bg: "bg.default",
    fontSize: "md",
    _focusVisible: {
      outlineWidth: "2px",
      outlineStyle: "solid",
      outlineColor: "border.emphasized",
    },
  }),
  walkInMsg: css({ fontSize: "sm", color: "fg.muted" }),
} as const;

// --- FloorMap scene (clamped-scale, pan container) ---------------------------------------------
export const mapStyles = {
  // The PAN container: a big/sparse room scrolls here instead of shrinking its tap targets.
  pan: css({
    width: "full",
    maxHeight: "60vh",
    overflow: "auto",
    borderWidth: "1px",
    borderColor: "border.default",
    rounded: "lg",
    bg: "bg.subtle",
  }),
  // The fixed-pixel scene box (sized `sceneWidth*pxPerUnit x sceneHeight*pxPerUnit`).
  scene: css({ position: "relative", mx: "auto" }),
  band: css({
    position: "absolute",
    rounded: "md",
    borderWidth: "1px",
    borderStyle: "dashed",
    borderColor: "border.default",
    bg: "bg.muted/40",
  }),
  bandLabel: css({
    position: "absolute",
    top: "1",
    left: "2",
    fontSize: "2xs",
    fontWeight: "semibold",
    letterSpacing: "wide",
    textTransform: "uppercase",
    color: "fg.muted",
  }),
  table: css({
    position: "absolute",
    display: "flex",
    flexDir: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "0",
    borderWidth: "2px",
    lineHeight: "1.1",
    fontWeight: "medium",
    overflow: "hidden",
    cursor: "pointer",
    transition: "box-shadow 0.15s, transform 0.15s",
    _disabled: { cursor: "default", opacity: 0.75 },
    _focusVisible: {
      outlineWidth: "2px",
      outlineColor: "colorPalette.default",
      outlineOffset: "1px",
    },
  }),
  // A seat target while a booking is armed: a clear accent ring + subtle lift so the tap zone reads.
  target: css({
    boxShadow: "0 0 0 3px var(--colors-color-palette-default)",
    colorPalette: "accent",
    cursor: "pointer",
    _hover: { transform: "scale(1.05)" },
  }),
  // The table a reassign was picked up from.
  source: css({ boxShadow: "0 0 0 3px var(--colors-booking-review-solid)" }),
  tableLabel: css({ fontWeight: "bold", fontSize: "xs" }),
  tableCap: css({ fontSize: "2xs", opacity: 0.85, fontVariantNumeric: "tabular-nums" }),
  tableParty: css({ fontSize: "2xs", fontWeight: "semibold", fontVariantNumeric: "tabular-nums" }),
  tableSince: css({ fontSize: "2xs", opacity: 0.8, fontVariantNumeric: "tabular-nums" }),
  stuckDot: css({
    fontSize: "2xs",
    fontWeight: "bold",
    color: "booking.noShow.fg",
    fontVariantNumeric: "tabular-nums",
  }),
} as const;
